/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import cloneDeep from "lodash-es/cloneDeep.js";
import isEqual from "lodash-es/isEqual.js";
import mergeWith from "lodash-es/mergeWith.js";
import { ServiceManager } from "../services/ServiceManager";
import { AppConfig } from "../../types/state/AppConfig";
import { AppState, ThemeState } from "../../types/state/AppState";
import { IS_PHONE } from "../utils/browserUtils";
import { CornersType } from "../utils/constants";
import {
  PerCornerConfig,
  ResolvedCornerConfig,
} from "../../types/config/CornersType";
import { PublicConfig } from "../../types/config/PublicConfig";
import { LanguagePack } from "../../types/config/LanguagePack";
import { DeepPartial } from "../../types/utilities/DeepPartial";
import { mergeCSSVariables } from "../utils/styleUtils";
import { reducers } from "./reducers";
import { AppStore, createAppStore } from "./appStore";
import {
  DEFAULT_CITATION_PANEL_STATE,
  DEFAULT_CUSTOM_PANEL_STATE,
  DEFAULT_IFRAME_PANEL_STATE,
  DEFAULT_INPUT_STATE,
  DEFAULT_LAUNCHER,
  DEFAULT_LAYOUT_STATE,
  DEFAULT_MESSAGE_PANEL_STATE,
  DEFAULT_MESSAGE_STATE,
  DEFAULT_PERSISTED_TO_BROWSER,
  DEFAULT_HUMAN_AGENT_STATE,
  DEFAULT_THEME_STATE,
  DEFAULT_HISTORY_PANEL_STATE,
  DEFAULT_WORKSPACE_PANEL_STATE,
  VIEW_STATE_ALL_CLOSED,
  VIEW_STATE_LAUNCHER_OPEN,
  VIEW_STATE_MAIN_WINDOW_OPEN,
  DEFAULT_HEADER,
} from "./reducerUtils";
import { enLanguagePack } from "../../types/config/LanguagePack";
import { LayoutConfig } from "../../types/config/LayoutConfig";
import { fromPersistableState } from "./persistenceUtils";

/**
 * Deep merge helper that:
 *  - ignores `undefined` in sources (keeps existing/default),
 *  - replaces arrays wholesale (no index-wise merge),
 *  - otherwise behaves like lodash merge.
 */
function mergeDefaultsDeep<T>(target: Partial<T>, ...sources: Partial<T>[]): T {
  return mergeWith(
    target as any,
    ...(sources as unknown as any[]),
    (objValue: unknown, srcValue: unknown) => {
      if (srcValue === undefined) {
        return objValue;
      }
      if (Array.isArray(objValue) && Array.isArray(srcValue)) {
        // Replace arrays instead of merging by index
        return srcValue.slice();
      }
      // Let mergeWith handle objects/primitives normally
      return undefined;
    },
  ) as T;
}

/**
 * Creates a complete AppConfig with derived values computed from the public config.
 */
function createAppConfig(publicConfig: PublicConfig): AppConfig {
  // Create the theme state with defaults applied and corners computed
  const themeWithDefaults: ThemeState = {
    originalCarbonTheme:
      publicConfig.injectCarbonTheme ?? DEFAULT_THEME_STATE.originalCarbonTheme,
    derivedCarbonTheme:
      publicConfig.injectCarbonTheme ?? DEFAULT_THEME_STATE.derivedCarbonTheme,
    aiEnabled: publicConfig.aiEnabled ?? DEFAULT_THEME_STATE.aiEnabled,
    corners: getThemeCornersType(publicConfig),
  };

  // Compute CSS variable overrides from theme configuration
  const cssVariableOverrides = mergeCSSVariables(
    publicConfig.layout?.customProperties || {},
    {},
    themeWithDefaults.derivedCarbonTheme,
    themeWithDefaults.aiEnabled,
  );

  // Build derived config using deep merge that skips undefined
  const derived = mergeDefaultsDeep(
    {},
    {
      header: DEFAULT_HEADER,
      layout: DEFAULT_LAYOUT_STATE,
      launcher: DEFAULT_LAUNCHER,
    },
    {
      header: publicConfig.header,
      layout: publicConfig.layout,
      launcher: publicConfig.launcher,
    },
  );

  return {
    public: publicConfig,
    derived: {
      cssVariableOverrides,
      themeWithDefaults,
      header: derived.header,
      layout: derived.layout,
      launcher: derived.launcher,
    },
  };
}

/**
 * Builds the active language pack: the `enLanguagePack` defaults overlaid with
 * any host-provided `strings`. The language pack is a flat key→string map, so a
 * shallow overlay is sufficient (and matches the dynamic-update path). Lives in
 * its own `AppState.languagePack` slice so a string change never churns the
 * config tree. Shared by boot, the dynamic `strings` effect, and the dynamic
 * config path so they all merge identically.
 */
function buildLanguagePack(
  strings?: DeepPartial<LanguagePack> | undefined,
): LanguagePack {
  return { ...enLanguagePack, ...(strings ?? {}) } as LanguagePack;
}

/**
 * Shallow-reconciles `next` against `prev` one level deep: any own key whose
 * value is deep-equal to the previous value reuses the previous reference. If
 * every key ends up reused and the key set is unchanged, the previous object is
 * returned wholesale so its reference survives too.
 *
 * Functions are compared by reference (lodash `isEqual` semantics), so a field
 * carrying a changed callback is treated as changed.
 *
 * Comparison is by value, not identity: two distinct but deep-equal instances
 * (a `Date`, `Map`, `Set`, or class instance with equal contents) are treated as
 * unchanged and the previous reference is reused. That is fine for the current
 * config shape (plain data + functions); if a field is ever added whose identity
 * matters beyond its value, it must not rely on this for change detection.
 *
 * Workload: called only on a runtime config replace (rare), with `prev`/`next`
 * being the `public` and `derived` slots of `AppConfig` — tens of keys at the
 * top level. The per-key `isEqual` deep walk is acceptable at that scale; if a
 * field is ever added whose sub-tree is large (e.g. a big lookup table), revisit.
 */
function reconcileObjectReferences<T extends object>(prev: T, next: T): T {
  if (!prev || prev === next) {
    return next;
  }

  const nextKeys = Object.keys(next);
  const prevKeys = Object.keys(prev);
  let allReused = nextKeys.length === prevKeys.length;

  const result: Record<string, unknown> = {};
  for (const key of nextKeys) {
    const nextValue = (next as Record<string, unknown>)[key];
    const prevValue = (prev as Record<string, unknown>)[key];
    if (
      Object.prototype.hasOwnProperty.call(prev, key) &&
      isEqual(prevValue, nextValue)
    ) {
      result[key] = prevValue;
    } else {
      result[key] = nextValue;
      allReused = false;
    }
  }

  return allReused ? prev : (result as T);
}

/**
 * Preserves object references for the parts of `next` that are value-equal to
 * `prev`, so a config change that only touches one field (e.g. toggling
 * `input.isDisabled`) does not hand every `config.public.*` / `config.derived.*`
 * sub-object — nor the `public` / `derived` / top-level `config` objects
 * themselves — a fresh reference. Selectors that read an unchanged sub-object
 * then keep their identity and skip re-rendering.
 *
 * `createAppConfig` always rebuilds the whole tree, so call this on its output
 * (against the previously stored `AppConfig`) before dispatching the replace.
 * At boot there is no previous config; pass `null` and `next` is returned as-is.
 */
function reconcileAppConfigReferences(
  prev: AppConfig | null | undefined,
  next: AppConfig,
): AppConfig {
  if (!prev) {
    return next;
  }

  const publicConfig = reconcileObjectReferences(prev.public, next.public);
  const derived = reconcileObjectReferences(prev.derived, next.derived);

  if (publicConfig === prev.public && derived === prev.derived) {
    return prev;
  }

  return { public: publicConfig, derived };
}

function createInitialState(config: AppConfig): AppState {
  // Input config (isVisible/isDisabled/isReadonly) is intentionally NOT mirrored
  // into state at boot. Those values are derived from config at read time via
  // the selectInput* selectors; assistantInputState only carries runtime
  // overrides, which start unset (null).
  const assistantInputState = { ...DEFAULT_INPUT_STATE };

  const persistedToBrowserStorage = cloneDeep(DEFAULT_PERSISTED_TO_BROWSER);

  const initialState: AppState = {
    // Config with derived values
    config,

    // Active language pack (defaults + host `strings`), kept off the config tree.
    languagePack: buildLanguagePack(config.public.strings),

    // Host markdown config; set from the `markdown` prop in ChatAppEntry.
    markdownConfig: undefined,

    // Messaging state
    ...DEFAULT_MESSAGE_STATE,

    // UI state
    suspendScrollDetection: false,
    showNonHeaderBackgroundCover: false,
    isRestarting: false,
    isBrowserPageVisible: true,

    // Input state
    assistantInputState,

    // Layout/responsive state
    chatWidthBreakpoint: null,
    chatWidth: null,
    chatHeight: null,

    // Lifecycle state
    isHydrated: false,
    viewChanging: false,
    initialViewChangeComplete: false,
    targetViewState:
      // If openChatByDefault is true we open on first load, otherwise we use the launcher.
      // This will be overwritten by session history if it exists.
      config.public.openChatByDefault
        ? VIEW_STATE_MAIN_WINDOW_OPEN
        : VIEW_STATE_LAUNCHER_OPEN,

    // Session state
    persistedToBrowserStorage,

    // Agent UI State
    humanAgentState: DEFAULT_HUMAN_AGENT_STATE,

    // Panel states
    iFramePanelState: DEFAULT_IFRAME_PANEL_STATE,
    viewSourcePanelState: DEFAULT_CITATION_PANEL_STATE,
    customPanelState: DEFAULT_CUSTOM_PANEL_STATE,
    workspacePanelState: DEFAULT_WORKSPACE_PANEL_STATE,
    historyPanelState: {
      ...DEFAULT_HISTORY_PANEL_STATE,
      // If startClosed is true, start closed everywhere
      // Otherwise, start open (will be adjusted by mobile detection)
      isOpen: config.public.history?.startClosed ? false : true,
    },
    responsePanelState: DEFAULT_MESSAGE_PANEL_STATE,
  };

  return initialState;
}

function doCreateStore(
  publicConfig: PublicConfig,
  serviceManager: ServiceManager,
): AppStore<AppState> {
  // Build the complete AppConfig with derived values
  const config = createAppConfig(publicConfig);

  const initialState: AppState = createInitialState(config);

  // When the host owns persistence (config.persistedState), boot from its initialState instead of
  // sessionStorage. Otherwise, pre-fill from session storage if a saved session exists.
  const persistedStateConfig = config.public.persistedState;
  const externallyPersisted = Boolean(
    persistedStateConfig?.initialState || persistedStateConfig?.onStateChange,
  );
  const sessionStorageState = externallyPersisted
    ? persistedStateConfig?.initialState
      ? fromPersistableState(persistedStateConfig.initialState)
      : null
    : serviceManager.userSessionStorageService?.loadSession();

  if (sessionStorageState) {
    // Use the viewState from session storage as the targetViewState. Note, this overwrites the value that was set for
    // targetViewState above, which took into account if openChatByDefault is true. This overwriting is intentional
    // since we only want those openChatByDefault to open the main window the first time the chat loads for a user.
    // After doCreateStore is finished Chat.startInternal() will try to change the view to this
    // targetViewState.
    initialState.targetViewState = sessionStorageState.viewState;
    // In order to keep the initial view state as the default view state we need to change the session storage
    // view state to the default before replacing the launcher state with the session storage state.
    sessionStorageState.viewState = VIEW_STATE_ALL_CLOSED;
    // Replace the launcher state with the session storage state.
    initialState.persistedToBrowserStorage = {
      ...initialState.persistedToBrowserStorage,
      ...sessionStorageState,
      // We only bother to show this on initial page load, so if we are getting something from session storage,
      // we set it to false.
      launcherShouldStartCallToActionCounterIfEnabled: false,
      disclaimersAccepted: {
        ...initialState.persistedToBrowserStorage.disclaimersAccepted,
        ...sessionStorageState?.disclaimersAccepted,
      },
      homeScreenState: {
        ...initialState.persistedToBrowserStorage.homeScreenState,
        ...sessionStorageState?.homeScreenState,
      },
      humanAgentState: {
        ...initialState.persistedToBrowserStorage.humanAgentState,
        ...sessionStorageState?.humanAgentState,
        responseUserProfiles: {
          ...initialState.persistedToBrowserStorage.humanAgentState
            .responseUserProfiles,
          ...sessionStorageState?.humanAgentState?.responseUserProfiles,
        },
      },
    };
  }

  if (typeof config.public.launcher?.showUnreadIndicator === "boolean") {
    initialState.persistedToBrowserStorage.showUnreadIndicator =
      config.public.launcher.showUnreadIndicator;
  }

  return createAppStore(reducerFunction, initialState);
}

/**
 * Checks if a corners configuration is a PerCornerConfig object.
 */
function isPerCornerConfig(
  corners: CornersType | PerCornerConfig | undefined,
): corners is PerCornerConfig {
  return (
    typeof corners === "object" &&
    corners !== null &&
    (corners.startStart !== undefined ||
      corners.startEnd !== undefined ||
      corners.endStart !== undefined ||
      corners.endEnd !== undefined)
  );
}

/**
 * Normalizes a corners configuration to a resolved per-corner configuration.
 * Handles both simple CornersType values and PerCornerConfig objects.
 */
function normalizeCorners(
  corners: CornersType | PerCornerConfig | undefined,
  defaultValue: CornersType,
): ResolvedCornerConfig {
  if (isPerCornerConfig(corners)) {
    // Per-corner config: use provided values or fall back to default
    return {
      startStart: corners.startStart ?? defaultValue,
      startEnd: corners.startEnd ?? defaultValue,
      endStart: corners.endStart ?? defaultValue,
      endEnd: corners.endEnd ?? defaultValue,
    };
  }

  // Simple config: apply the same value to all corners
  const cornerValue = corners ?? defaultValue;
  return {
    startStart: cornerValue,
    startEnd: cornerValue,
    endStart: cornerValue,
    endEnd: cornerValue,
  };
}

/**
 * Returns the resolved corner configuration for the Carbon AI Chat widget.
 */
function getThemeCornersType(publicConfig: PublicConfig): ResolvedCornerConfig {
  const layoutState = getLayoutState(publicConfig);

  // If frame is disabled or on phone, force all corners to square
  if (layoutState.showFrame === false || IS_PHONE) {
    return {
      startStart: CornersType.SQUARE,
      startEnd: CornersType.SQUARE,
      endStart: CornersType.SQUARE,
      endEnd: CornersType.SQUARE,
    };
  }

  // If corners is explicitly set to SQUARE (simple config), force all to square
  if (publicConfig.layout?.corners === CornersType.SQUARE) {
    return {
      startStart: CornersType.SQUARE,
      startEnd: CornersType.SQUARE,
      endStart: CornersType.SQUARE,
      endEnd: CornersType.SQUARE,
    };
  }

  // Otherwise, normalize the corners configuration
  return normalizeCorners(
    publicConfig.layout?.corners,
    DEFAULT_THEME_STATE.corners.startStart, // Use default from one corner
  );
}

function getLayoutState(publicConfig: PublicConfig): LayoutConfig {
  // Use the same deep merge semantics for layout specifically
  return mergeDefaultsDeep<LayoutConfig>(
    {},
    DEFAULT_LAYOUT_STATE,
    publicConfig.layout ?? {},
  );
}

/**
 * This is the global reducer for the redux store. It will use the map of reducers from the "reducers" array to map
 * the action type to the sub-reducer for that specific action.
 */
function reducerFunction(
  state: AppState,
  action: { type: string; [key: string]: unknown } | undefined,
): AppState {
  return action && reducers[action.type]
    ? reducers[action.type](state, action)
    : state;
}

export {
  doCreateStore,
  createAppConfig,
  buildLanguagePack,
  reconcileAppConfigReferences,
  createInitialState,
};
